import { Select } from "components/inputs";
import debounce from "lodash/debounce";
import { useEffect, useMemo, useState } from "react";
import { useGetLienDirectoryEntriesQuery } from "services/lien-api";
import { logToSentry } from "utils/sentry";

import type { LienDirectoryCompanyRef } from "../../../LienTypes";
import { formatDirectoryCompanyLabel } from "../../../lien-utils";

type DirectoryCompanySectionProps = {
  handleFieldChange: (field: string | null, value: unknown) => Promise<void>;
  currentDirectoryCompany?: LienDirectoryCompanyRef | null;
};

type OptionType = {
  label: string;
  value: string;
};

/** Search is server-side; a query needs at least this many characters. */
export const MIN_SEARCH_LENGTH = 2;
export const SEARCH_DEBOUNCE_MS = 500;
export const SEARCH_RESULT_LIMIT = 50;

const toOption = (entry: {
  id: number;
  legal_entity_name: string;
  contract_number?: string | null;
}): OptionType => ({
  label: formatDirectoryCompanyLabel(entry),
  value: entry.id.toString(),
});

const DirectoryCompanySection = ({
  handleFieldChange,
  currentDirectoryCompany,
}: DirectoryCompanySectionProps) => {
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [selectedOption, setSelectedOption] = useState<OptionType | null>(null);

  const debouncedSetSearchTerm = useMemo(
    () =>
      debounce((value: string) => {
        setSearchTerm(value.trim());
      }, SEARCH_DEBOUNCE_MS),
    [],
  );

  useEffect(
    () => () => debouncedSetSearchTerm.cancel(),
    [debouncedSetSearchTerm],
  );

  const currentOption = useMemo(
    () =>
      currentDirectoryCompany?.id && currentDirectoryCompany.legal_entity_name
        ? toOption(currentDirectoryCompany)
        : null,
    [currentDirectoryCompany],
  );

  // initialize selected option from current directory company
  useEffect(() => {
    setSelectedOption(currentOption);
  }, [currentOption]);

  const hasSearchTerm = searchTerm.length >= MIN_SEARCH_LENGTH;

  const {
    data: directoryEntries,
    isFetching: isDirectoryFetching,
    error,
  } = useGetLienDirectoryEntriesQuery(
    { searchTerm, limit: SEARCH_RESULT_LIMIT },
    { skip: !hasSearchTerm },
  );

  useEffect(() => {
    if (hasSearchTerm && error) {
      // the RTK Query middleware already toasts 5xx / handles auth errors;
      // record the search failure without dumping the response body
      logToSentry("Directory company search failed", "warn", {
        status: "status" in error ? error.status : undefined,
        searchTerm,
      });
    }
  }, [hasSearchTerm, error, searchTerm]);

  const directoryOptions = useMemo<OptionType[]>(() => {
    if (!hasSearchTerm) {
      // nothing to search for yet, but keep the current selection visible
      return currentOption ? [currentOption] : [];
    }
    if (error) {
      return [];
    }
    return directoryEntries?.results?.map(toOption) ?? [];
  }, [hasSearchTerm, currentOption, error, directoryEntries]);

  const handleDirectoryChange = async (option: OptionType | null) => {
    setSelectedOption(option);
    const directoryCompanyId = option ? parseInt(option.value, 10) : null;
    await handleFieldChange("directory_company", directoryCompanyId);
  };

  return (
    <>
      <h3>Directory Company</h3>
      <Select
        name="directory_company"
        label="Select Directory Company"
        options={directoryOptions}
        value={selectedOption}
        placeholder="Plan name or contract number..."
        // the label carries the contract number, which is the disambiguator;
        // let the value use the whole control and reveal the full label on hover
        widthVariant="full"
        tooltip={selectedOption?.label}
        // results are already filtered by the API (plan name AND/OR contract
        // number); react-select's default substring filter would hide matches
        // for multi-term queries like "aetna H2056"
        filterOption={() => true}
        isLoading={hasSearchTerm && isDirectoryFetching}
        noOptionsMessage={({ inputValue }) =>
          !inputValue || inputValue.trim().length < MIN_SEARCH_LENGTH
            ? "Type at least 2 characters of a plan name or contract number..."
            : isDirectoryFetching
              ? "Searching..."
              : error
                ? "Error loading directory companies. Please try again."
                : "No directory companies found."
        }
        onInputChange={(inputValue: string) =>
          debouncedSetSearchTerm(inputValue)
        }
        onChange={handleDirectoryChange}
        isClearable
      />
    </>
  );
};

export default DirectoryCompanySection;
